import { useOrgSlug } from '@/hooks/use-org-slug';
import { useProcessingApplication } from '@/hooks/use-processing-application';
import { getTagadaDashboardUrl, getTagadaTpaDashboardUrl } from '@/lib/tagadapay-dashboard';

/** What the CRM should offer a merchant about TagadaPay processing. */
export type ApplyIntent = 'apply' | 'finish' | 'view';

export interface TagadapayApply {
  isLoading: boolean;
  /**
   * `apply`  — never applied (or was declined): offer the application.
   * `finish` — a draft is waiting on the dashboard.
   * `view`   — already submitted / approved / live: NEVER ask them to apply
   *            again, just take them to the application they already have.
   */
  intent: ApplyIntent;
  /** True only for `apply` — the one case where "I need processing" still makes sense. */
  canApply: boolean;
  href: string;
}

/**
 * Single source of truth for every "get TagadaPay processing" call to action in
 * the CRM. A merchant whose TPA is pending or active has already been through
 * the application; showing them an apply button again is how they end up
 * submitting twice or believing nothing was recorded.
 */
export function useTagadapayApply(): TagadapayApply {
  const { state, application, isLoading } = useProcessingApplication();
  const { urlSlug } = useOrgSlug();

  const canApply = state === 'none' || state === 'rejected';
  const intent: ApplyIntent = canApply ? 'apply' : state === 'draft' ? 'finish' : 'view';

  const href = canApply
    ? getTagadaDashboardUrl('/merchant/requests/new', { from: 'crm', org: urlSlug })
    : application?.tpaId
      ? getTagadaTpaDashboardUrl(application.tpaId)
      : getTagadaDashboardUrl('/merchant/requests', { from: 'crm', org: urlSlug });

  return { isLoading, intent, canApply, href };
}
